import { Update } from '@ngrx/entity';
import { createAction, props } from '@ngrx/store';
import { CpRootNode, IVirtualPanelItem, Json } from '@se/cp.common';
import { UserEx } from '@se/models';
import { Addendum, ButtonClick, CustomPageType } from '@se/platform';
import { PropertyType } from '../cp-configuration/view-model';
import { CpFlatNode, CpNodeFilter, CpNodeFilterStatuses } from '../cp-node';
import { SignAfterSaveParams } from '../helpers';
import { CpDialogState, CpPageState } from './cp.state';

export interface CpInitializePayload {
    page: CpPageState;
    toolbar?: { description1?: string };
    isPreview?: boolean;
}

export const cpInitializePanelsService = createAction('[Cp] initPanelsService');

export const cpAddPanel = createAction('[Cp] addPanel', props<{ parentNodeId: string; panel: IVirtualPanelItem }>());

export const cpAddNodes = createAction('[Cp] AddNodes', props<{ nodes: CpFlatNode[]; parentId: string }>());

export const cpAddNodesToRoot = createAction('[Cp] AddNodesToRoot', props<{ nodes: CpFlatNode[] }>());

export const cpApplyCalculations = createAction('[Cp] ApplyCalculations');

export const cpApplyNormalValues = createAction('[Cp] ApplyNormalValues', props<{ notifySuccess?: true }>());

export const cpDialogButtonClick = createAction('[Cp] DialogButtonClick', props<{ button: ButtonClick }>());

export const cpEmpty = createAction('[Cp] EMPTY');

export const cpFilterNodes = createAction('[Cp] FilterNodes', props<{ filter: CpNodeFilter }>());

export const cpFilterTextChange = createAction('[Cp] FilterTextChange', props<{ text: string }>());

export const cpInitialize = createAction('[Cp] Initialize', props<CpInitializePayload>());

export const cpInitializeNodes = createAction(
    '[Cp] InitializeNodes',
    props<{ nodes: CpFlatNode[]; rootNode?: CpRootNode }>(),
);

export const cpNavSelectNode = createAction('[Cp] NavSelectNode', props<{ id: string }>());

export const cpOpenImagesForCurrentExamination = createAction(
    '[Cp] OpenImagesForCurrentExamination',
    props<{ customPageType: CustomPageType | undefined }>(),
);

export const cpOpenLinkedEcgs = createAction('[Cp] OpenLinkedEcgs');

export const cpRemoveNodes = createAction('[Cp] RemoveNodes', props<{ nodes: string[] }>());

export const cpPatchModel = createAction('[Cp] PatchModel', props<{ patches: Record<string, PropertyType> }>());

export const cpRefreshModelFromServer = createAction(
    '[Cp] RefreshModelFromServer',
    props<{ addedExaminationIds?: string[]; removedExaminationIds?: string[] }>(),
);

export const cpSafeExit = createAction('[Cp] SafeExit');

export const cpSelectNode = createAction('[Cp] SelectNode', props<{ id: string }>());

export const cpSetDialogConfig = createAction('[Cp] SetDialogConfig', props<{ config: Partial<CpDialogState> }>());

export const cpSetModel = createAction('[Cp] SetModel', props<{ model: Json }>());

export const cpShowErrorModal = createAction('[Cp] ShowErrorModal', props<{ message: string; title: string }>());

export const cpShowWarningModal = createAction('[Cp] ShowWarningModal', props<{ message: string; title: string }>());

export const cpToggleFilterButton = createAction(
    '[Cp] ToggleFilterButton',
    props<{ status: keyof CpNodeFilterStatuses }>(),
);

export const cpUpdateAllNodes = createAction('[Cp] UpdateAllNodes', props<Partial<CpFlatNode>>());

export const cpUpdateDialogConfig = createAction(
    '[Cp] UpdateDialogConfig',
    props<{ config: Partial<CpDialogState> }>(),
);

export const cpUpdateNode = createAction('[Cp] UpdateNode', props<Update<CpFlatNode>>());

export const cpUpdateNodes = createAction('[Cp] EditNodes', props<{ nodes: Update<CpFlatNode>[] }>());

// #region Toolbar Actions

export const cpAddExamToReport = createAction('[Cp] AddExamToReport');

export const cpApprove = createAction('[Cp] Approve', props<{ useExamService?: boolean; print?: boolean }>());

export const cpApproveAddendum = createAction(
    '[Cp] ApproveAddendum',
    props<{ addendum?: Addendum; referringPhysician?: UserEx; print?: boolean }>(),
);

export const cpApproveSaving = createAction(
    '[Cp] ApproveSaving',
    props<{ useExamService?: boolean; print?: boolean }>(),
);

export const cpNavigationBack = createAction('[Cp] NavigationBack', props<{ requestConfirmation?: boolean }>());

export const cpRemoveExamFromReport = createAction('[Cp] RemoveExamFromReport');

export const cpSave = createAction(
    '[Cp] Save',
    props<{
        forcedReload?: boolean;
        useExamService?: boolean;
        signParams?: SignAfterSaveParams;
        researchArchive?: boolean;
    }>(),
);

export const cpSaveCompleted = createAction('[Cp] SaveCompleted', props<{ error: boolean }>());

export const cpSign = createAction(
    '[Cp] Sign',
    props<{ print?: boolean; addendum?: Addendum; referringPhysician?: UserEx; justSaved?: boolean }>(),
);

export const cpUndoValidation = createAction('[Cp] UndoValidation');

export const cpObscurationEditRequest = createAction('[Cp] ObscurationEditRequest');

// #endregion

export const cpClinicalEvaluationMessage = createAction(
    '[Cp] cpClinicalEvaluationMessage',
    props<{ binding: string; warning: string | null; suggestion: string | null }>(),
);

export const cpAskClinicalEvaluationMessage = createAction(
    '[Cp] cpAskClinicalEvaluationMessage',
    props<{ binding: string }>(),
);

export const cpRefreshNodesStatus = createAction('[Cp] cpRefreshNodesStatus');

export const cpViewAnamnesticData = createAction('[Cp] cpViewAnamnesticData');

export const cpDefinitiveAndPrint = createAction('[Cp cpDefinitiveAndPrint');

export const cpTeleconsultationViewRequest = createAction('[Cp] cpTeleconsultationViewRequest');

export const cpRestartEcoCVServices = createAction('[Cp] cpRestartEcoCVServices');
